/**
 * Browser-local documentation history, kept per signed-in user, under the
 * same rules as the products history (see recentProducts.ts in
 * @univerus/plugin-products): no network requests, one key per user entity
 * ref, and every other user's history deleted on first read or write.
 *
 * One entry per entity, not per page: reading any page of an entity's docs
 * moves that entity to the front and links back to its docs root. Only the
 * entity ref and visit time are stored; titles and descriptions are looked up
 * in the catalog when shown, so they stay current and permission-checked.
 */
export interface RecentDoc {
  namespace: string;
  kind: string;
  name: string;
  viewedAt: number;
}
export type RecentDocRef = Omit<RecentDoc, 'viewedAt'>;
const PREFIX = 'univerus-hub.docs.recent.v2:';
/** The first format, which also stored titles and descriptions. */
const LEGACY_PREFIX = 'univerus-hub.docs.recent.v1:';
export const RECENT_DOCS_CHANGED = 'univerus-docs-history';
const LIMIT = 12;
// An entity ref segment that is safe to put in a path.
const SEGMENT = /^(?!\.{1,2}$)[a-z0-9._-]+$/;

const keyFor = (userRef: string) => `${PREFIX}${userRef}`;
const idOf = (doc: RecentDocRef) => `${doc.namespace}/${doc.kind}/${doc.name}`;

/** The entity's docs root; the reader picks the default tier and version. */
export const recentDocPath = (doc: RecentDocRef) => `/docs/${idOf(doc)}/`;

/** The catalog entity ref, e.g. `component:default/jira-mcp`. */
export const recentDocEntityRef = (doc: RecentDocRef) =>
  `${doc.kind}:${doc.namespace}/${doc.name}`;

/** Deletes every other user's history and every legacy-format history. */
function dropOtherHistories(userRef: string) {
  const mine = keyFor(userRef);
  const stale: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (!key) continue;
    if (
      (key.startsWith(PREFIX) && key !== mine) ||
      key.startsWith(LEGACY_PREFIX)
    )
      stale.push(key);
  }
  for (const key of stale) window.localStorage.removeItem(key);
}

export function readRecentDocs(userRef: string): RecentDoc[] {
  try {
    dropOtherHistories(userRef);
    const value: unknown = JSON.parse(
      window.localStorage.getItem(keyFor(userRef)) || '[]',
    );
    if (!Array.isArray(value)) return [];
    const seen = new Set<string>();
    return (
      value
        .filter((item): item is RecentDoc => {
          if (
            !item ||
            typeof item.namespace !== 'string' ||
            !SEGMENT.test(item.namespace) ||
            typeof item.kind !== 'string' ||
            !SEGMENT.test(item.kind) ||
            typeof item.name !== 'string' ||
            !SEGMENT.test(item.name) ||
            typeof item.viewedAt !== 'number' ||
            !Number.isFinite(item.viewedAt) ||
            seen.has(idOf(item))
          )
            return false;
          seen.add(idOf(item));
          return true;
        })
        // Keep stored fields only, should an entry carry anything else.
        .map(({ namespace, kind, name, viewedAt }) => ({
          namespace,
          kind,
          name,
          viewedAt,
        }))
        .sort((a, b) => b.viewedAt - a.viewedAt)
        .slice(0, LIMIT)
    );
  } catch {
    return [];
  }
}

export function rememberDoc(userRef: string, doc: RecentDocRef): void {
  try {
    // TechDocs paths are lowercase (no legacy case-sensitive triplet paths).
    const entry: RecentDoc = {
      namespace: doc.namespace.toLocaleLowerCase('en-US'),
      kind: doc.kind.toLocaleLowerCase('en-US'),
      name: doc.name.toLocaleLowerCase('en-US'),
      viewedAt: Date.now(),
    };
    const items = [
      entry,
      ...readRecentDocs(userRef).filter(d => idOf(d) !== idOf(entry)),
    ].slice(0, LIMIT);
    window.localStorage.setItem(keyFor(userRef), JSON.stringify(items));
    window.dispatchEvent(new Event(RECENT_DOCS_CHANGED));
  } catch {
    /* Storage denial must never prevent reading docs. */
  }
}
